import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import "./Overview.styles.scss";

// Type Imports
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";
import { IOrgMetadata } from "../../context/context.types";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import { IAssetDefinition } from "@pulse-ui/codegen/generated_ts/refdata";
import { IAGOverviewDataRow } from "@pulse-ui/types/components/AGTable";
import { ColDef, SideBarDef } from "ag-grid-community";
import { ModifiedAccountOverview } from "./Overview.types";
import { IUserPreferencesState } from "@pulse-ui/types/components/ToolPanel";

// Component Imports
import { AgGridReact } from "ag-grid-react";
import { AGTable, AlertMessage } from "../../components";
import { Loading } from "../../layout";
import ViewsToolPanel from "../../components/AGToolPanels/ViewsToolPanel/ViewsToolPanel";
import PreferencesPanel from "@/components/AGToolPanels/PreferencesPanel/PreferencesPanel";
import AccountStatusCell from "@/components/AGCellRenderers/AccountStatusCell";

// Material UI Imports
import { SvgIcon, Switch } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utcPlugin from "dayjs/plugin/utc";

// Package Imports
import { v4 as uuidv4 } from "uuid";

// Context
import { useAppContext } from "../../context/context";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";

// Utils/Promise/Helper Function Imports
import {
  getOrgMetadata,
  getOrgs,
  getPricingAssets,
  getUserStates,
  handleGetOverviewData,
  setOrgMetadata,
  setPreferenceContext,
} from "../../utils/promises";
import {
  currencyFormatter,
  customNumAggFunc,
  customNumericSortFunc,
  customStatusAggFunc,
  customTextAggFunc,
  numericFormatterMillions,
} from "../../utils/helper_functions";
import { useAlert } from "@/utils/hooks/useAlert/useAlert";

const convertStartTimeParamToDayjs = (start_time: string | null) => {
  const split_time = start_time?.split("-");
  const hour = split_time && split_time[0] ? parseInt(split_time[0]) : null;
  const minutes = split_time && split_time[1] ? split_time[1] : null;

  if (hour && minutes) {
    return dayjs()
      .hour(hour)
      .minute(parseInt(minutes))
      .second(0)
      .millisecond(0);
  } else {
    return null;
  }
};

const Overview = () => {
  // State - START -------------------------------------
  // Query Params States
  const USDT = "USDT";
  const MILLIONS = "Millions";
  const DOLLAR = "Dollar";
  const QUOTE_ASSET = "quote_asset";
  const INTERVAL = "interval";
  const START_TIME = "start_time";
  const ONEDAY = "oneday";
  const PREFERENCES_STATE_ID = "7bb0d309-eecd-4413-bfa6-73c5bd4da8b3";

  const [_, setQueryParams] = useSearchParams({
    quote_asset: "",
    interval: "oneday",
    start_time: "",
  });
  const location_origin = window.location.origin;
  const gridRef = useRef<AgGridReact<IAGOverviewDataRow> | null>(null);
  const [rowData, setRowData] = useState<IAGOverviewDataRow[]>([]);
  const [displayTable, setDisplayTable] = useState<boolean>(false);
  dayjs.extend(utcPlugin);
  const [loading, setLoading] = useState<boolean>(true);
  const [refdataAssets, setRefdataAssets] = useState<ISelectOption[]>([]);
  const [millionsFormatting, setMillionsFormatting] = useState<
    typeof MILLIONS | typeof DOLLAR
  >(MILLIONS);

  // Error Handling / Alert Message States
  const { alerts, pushAlert, closeAlert } = useAlert();

  // Context States
  const { context_state, update_org_metadata, set_user_preferences } =
    useAppContext();
  // State - END ---------------------------------------

  // Methods - START -----------------------------------
  const generateDisplayNames = (org_metadata: IOrgMetadata) => {
    const temp_map: Map<string, string> = new Map();

    for (let portfolio of org_metadata.portfolios) {
      temp_map.set(portfolio.portfolio_id, portfolio.display_name);

      if (portfolio.accounts) {
        for (let account of portfolio.accounts) {
          temp_map.set(account.account_id, account.display_name);
        }
      }
    }

    return temp_map;
  };

  const handleInitialDataLoad = async () => {
    setLoading(true);

    try {
      const get_orgs_res = await getOrgs(location_origin);
      const get_org_metadata_res = await getOrgMetadata(
        get_orgs_res.data.org_id,
        location_origin
      );
      await setOrgMetadata(get_org_metadata_res.data, update_org_metadata);

      const get_pricing_assets_res = await getPricingAssets(location_origin);

      const assets_map = get_pricing_assets_res.data.map(
        (asset: IAssetDefinition) => {
          return {
            label: asset,
            value: asset,
          };
        }
      );

      setRefdataAssets(assets_map);

      if (context_state.org_metadata.portfolios) {
        const user_default_time_state = await fetchUserPreferencesState();

        const fetched_user_preferences_state =
          user_default_time_state?.grid_state || {
            quote_asset: USDT,
            interval: ONEDAY,
            start_time: null,
          };

        await setPreferenceContext(
          fetched_user_preferences_state,
          set_user_preferences
        );

        await overviewDataHandler(
          fetched_user_preferences_state.quote_asset || USDT,
          convertStartTimeParamToDayjs(
            fetched_user_preferences_state.start_time
          ) || null,
          fetched_user_preferences_state.interval || ONEDAY
        );
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const parseAndSetTableData = (table_data: ModifiedAccountOverview[]) => {
    const display_names = generateDisplayNames(context_state.org_metadata);

    let temp_row_data_arr: IAGOverviewDataRow[] = [];

    for (let item of table_data) {
      for (let key in item) {
        if (key === "portfolio_id") continue;

        let el = item[key] as ModifiedAccountOverview;

        let row: IAGOverviewDataRow = {
          row_id: uuidv4(),
          account: el?.account || "-",
          account_display_name: display_names.get(el.account) || "-",
          portfolio: el?.portfolio || "-",
          portfolio_display_name: display_names.get(el.portfolio) || "-",
          gmv: el.gmv?.toLocaleString() || "-",
          leverage: el.leverage?.toLocaleString() || "-",
          volume: el.volume?.toLocaleString() || "-",
          current_account_equity: el.current_account_equity
            ? el.current_account_equity?.toLocaleString()
            : "-",
          trailing_account_equity: el.trailing_account_equity
            ? el.trailing_account_equity?.toLocaleString()
            : "-",
          pnl: el.pnl?.toLocaleString() || "-",
          strategy: el?.strategy || "-",
          account_status: el.account_status?.toString() || null,
          account_status_message: el.account_status_message,
        };

        temp_row_data_arr.push(row);
      }
    }

    setRowData(temp_row_data_arr);
  };

  const generateRequestPromises = (
    quoteAsset: string,
    window: number | null
  ) => {
    return context_state.org_metadata.portfolios.map((portfolio) => {
      if (portfolio.accounts) {
        if (portfolio.accounts.length > 0 && quoteAsset) {
          return handleGetOverviewData(
            portfolio,
            quoteAsset,
            window,
            location_origin
          )
            .then((handle_get_overview_data_res) => {
              if (handle_get_overview_data_res.status === 204) {
                pushAlert({
                  id: uuidv4(),
                  message: "No Overview rows found.",
                  status: 204,
                });
              }
              return handle_get_overview_data_res.data;
            })
            .catch((handle_get_overview_data_error) => {
              pushAlert({
                id: uuidv4(),
                message:
                  handle_get_overview_data_error?.error ||
                  "Error encountered while fetching overview data.",
                status: handle_get_overview_data_error?.status || 500,
              });
              return null;
            });
        } else {
          return Promise.resolve(null);
        }
      }
    });
  };

  const getStartTimeWindow = (
    _type: string | null,
    picker_value: Dayjs
  ): number => {
    const now = dayjs();

    const modified_now = now.second(0).millisecond(0);
    const modified_now_unix_seconds = modified_now.unix();

    const last_timestamp_seconds = now.isBefore(picker_value)
      ? picker_value.subtract(1, "day").unix()
      : picker_value.unix();

    const window_seconds = modified_now_unix_seconds - last_timestamp_seconds;
    const window_minutes = window_seconds
      ? Math.ceil((window_seconds - 60) / 60)
      : 0;

    return window_minutes;
  };

  const overviewDataHandler = (
    quoteAsset: string,
    start_from_time_picker_value: Dayjs | null,
    interval_value: string | null
  ): Promise<{ status: number; message: any }> => {
    return new Promise((resolve, _) => {
      setLoading(true);

      let handle_get_overview_data_res_arr: ModifiedAccountOverview[] = [];
      let promises: any = generateRequestPromises(
        quoteAsset,
        start_from_time_picker_value
          ? getStartTimeWindow(interval_value, start_from_time_picker_value)
          : 0
      );

      Promise.all(promises)
        .then((results) => {
          handle_get_overview_data_res_arr = results.filter(
            (res) => res !== null
          );

          resolve({ status: 200, message: null });
        })
        .finally(() => {
          parseAndSetTableData(handle_get_overview_data_res_arr);
          setLoading(false);
        });
    });
  };

  const createLeverageValueObject = (gmv: any, current_account_equity: any) => {
    return {
      gmv: gmv,
      current_account_equity,
      toString: () =>
        `${
          gmv && current_account_equity
            ? (gmv / current_account_equity).toFixed(1)
            : 0
        }`,
    };
  };

  const leverageValueGetter = (params: any) => {
    if (!(params.node && params.node.group)) {
      return createLeverageValueObject(
        parseFloat(params.data.gmv),
        parseFloat(params.data.current_account_equity)
      );
    }
  };

  const leverageAggFunc = (params: any) => {
    let gmvSum = 0;
    let accountEquitySum = 0;
    params.values.forEach((value: any) => {
      if (value && value.gmv) {
        const roundedGmv = Math.floor(value.gmv * 10) / 10;
        gmvSum += roundedGmv;
      }
      if (value && value.current_account_equity) {
        const roundedAccountEquity =
          Math.floor(value.current_account_equity * 10) / 10;
        accountEquitySum += roundedAccountEquity;
      }
    });
    return createLeverageValueObject(gmvSum, accountEquitySum);
  };

  const leverageFormatter = (params: any) => {
    if (!params.value || params.value === 0) return "-";
    return "" + Math.round(params.value * 100) / 100;
  };

  /**
   * Fetches default time state for the current user from the server.
   * Updates the userTimeState with the retrieved data or displays
   * an error message if the fetch fails.
   *
   * @async
   * @function fetchUserPreferencesState
   * @returns {Promise<void>}
   *
   * @example
   * // Call the function to fetch default time state
   * await fetchUserState();
   *
   * @throws {Error} Pushes an alert message if the API call fails
   */
  const fetchUserPreferencesState = async () => {
    try {
      // Make API call to retrieve user's saved default time state
      const get_user_state_res = await getUserStates(
        location_origin,
        PREFERENCES_STATE_ID
      );

      const userPreferencesState = get_user_state_res.data;

      if (userPreferencesState && userPreferencesState.length > 0) {
        // Update context with the fetched preferences state
        const parsed_grid_state_field = JSON.parse(
          userPreferencesState[0].grid_state
        );
        const user_preferences_state: IUserPreferencesState = {
          ...userPreferencesState[0],
          grid_state: parsed_grid_state_field,
        };

        setPreferenceContext(
          user_preferences_state.grid_state,
          set_user_preferences
        );
        return user_preferences_state;
      } else {
        setPreferenceContext(
          {
            quote_asset: USDT,
            interval: ONEDAY,
            start_time: null,
          },
          set_user_preferences
        );
        return null;
      }
    } catch (error_res: any) {
      // Handle any errors by displaying an alert message to the user
      pushAlert({
        id: uuidv4(), // Generate unique ID for the alert
        message: error_res.message, // Use the error message from the caught error
        status: error_res?.status || 500, // Use the status code from the caught error
      });

      return null;
    }
  };

  // Column Definitions: Defines & controls grid columns.
  const initialColDefs: ColDef<IAGOverviewDataRow>[] = [
    {
      headerName: "Status",
      field: "account_status",
      cellClass: "ag-center-aligned-cell",
      cellRenderer: AccountStatusCell,
      cellRendererParams: (params: any) => {
        if (!params) {
          return {
            status: null,
            status_message: null,
          };
        }

        // For group rows, use the aggregated value
        if (params.node?.group) {
          return {
            status: params.value || null,
            status_message: null,
          };
        }

        // For detail rows, use the data from the row
        if (!params.data) {
          return {
            status: null,
            status_message: null,
          };
        }

        return {
          status: params.data.account_status || null,
          status_message: params.data.account_status_message || null,
        };
      },
      aggFunc: customStatusAggFunc,
    },
    {
      headerName: "$Account Equity (mm)",
      field: "current_account_equity",
      aggFunc: (params: any) => customNumAggFunc(params, 2),
      valueFormatter: (params: any) => numericFormatterMillions(params, "$"),
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "$Account Equity -24h (mm)",
      field: "trailing_account_equity",
      aggFunc: (params: any) => customNumAggFunc(params, 2),
      valueFormatter: (params: any) => numericFormatterMillions(params, "$"),
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "$Gmv (mm)",
      field: "gmv",
      aggFunc: (params: any) => customNumAggFunc(params, 2),
      valueFormatter: (params: any) => numericFormatterMillions(params, "$"),
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "Leverage",
      field: "leverage",
      aggFunc: leverageAggFunc,
      valueGetter: leverageValueGetter,
      valueFormatter: leverageFormatter,
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "$P&L",
      field: "pnl",
      aggFunc: (params: any) => customNumAggFunc(params, 2),
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "$Volume (mm)",
      field: "volume",
      aggFunc: (params: any) => customNumAggFunc(params, 2),
      valueFormatter: (params: any) => numericFormatterMillions(params, "$"),
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "Portfolio Name",
      field: "portfolio_display_name",
      aggFunc: customTextAggFunc,
      cellClass: "ag-center-aligned-cell",
      rowGroupIndex: 0,
    },
    {
      headerName: "Portfolio ID",
      field: "portfolio",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
      hide: true,
    },
    {
      headerName: "Account Name",
      field: "account_display_name",
      aggFunc: customTextAggFunc,
      cellClass: "ag-center-aligned-cell",
    },
    {
      headerName: "Account ID",
      field: "account",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
      hide: true,
    },
    {
      headerName: "Strategy",
      field: "strategy",
      aggFunc: customTextAggFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ];

  const currencyFormatterColDefs: ColDef<IAGOverviewDataRow>[] = [
    {
      headerName: "$Account Equity",
      field: "current_account_equity",
      aggFunc: (params: any) => customNumAggFunc(params, 2),
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "$Account Equity -24h",
      field: "trailing_account_equity",
      aggFunc: (params: any) => customNumAggFunc(params, 2),
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "$Gmv",
      field: "gmv",
      aggFunc: (params: any) => customNumAggFunc(params, 2),
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "Leverage",
      field: "leverage",
      aggFunc: leverageAggFunc,
      valueGetter: leverageValueGetter,
      valueFormatter: leverageFormatter,
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "$P&L",
      field: "pnl",
      aggFunc: (params: any) => customNumAggFunc(params, 2),
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "$Volume",
      field: "volume",
      aggFunc: (params: any) => customNumAggFunc(params, 2),
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      cellClass: "ag-center-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "Portfolio Name",
      field: "portfolio_display_name",
      aggFunc: customTextAggFunc,
      cellClass: "ag-center-aligned-cell",
      rowGroupIndex: 0,
    },
    {
      headerName: "Portfolio ID",
      field: "portfolio",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
      hide: true,
    },
    {
      headerName: "Account Name",
      field: "account_display_name",
      aggFunc: customTextAggFunc,
      cellClass: "ag-center-aligned-cell",
    },
    {
      headerName: "Account ID",
      field: "account",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
      hide: true,
    },
    {
      headerName: "Strategy",
      field: "strategy",
      aggFunc: customTextAggFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ];

  const [colDefs] = useState<ColDef<IAGOverviewDataRow>[]>(initialColDefs);

  const defaultColDef: ColDef = {
    filter: true,
    enableRowGroup: true,
  };

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: "Group",
      minWidth: 280,
      filter: "agTextColumnFilter",
      cellRendererParams: {
        suppressCount: true,
      },
    };
  }, []);

  const sideBar = useMemo<
    SideBarDef | string | string[] | boolean | null
  >(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
        {
          id: "viewsToolPanel",
          labelDefault: "Views",
          labelKey: "viewsToolPanel",
          iconKey: "viewsIcon",
          toolPanel: ViewsToolPanel,
          toolPanelParams: {
            title: "Views",
            page: "overview",
            setLoading: setLoading,
            pushAlert: pushAlert,
          },
        },
        {
          id: "preferencesPanel",
          labelDefault: "Preferences",
          labelKey: "preferencesPanel",
          iconKey: "preferencesIcon",
          toolPanel: PreferencesPanel,
          toolPanelParams: {
            title: "Preferences",
            titleIcon: "preferences",
            refdataAssets: refdataAssets,
            PREFERENCES_STATE_ID: PREFERENCES_STATE_ID,
            USDT: USDT,
            ONEDAY: ONEDAY,
            fetchUserState: fetchUserPreferencesState,
            setLoading: setLoading,
            pushAlert: pushAlert,
            overviewDataHandler: overviewDataHandler,
            convertStartTimeParamToDayjs: convertStartTimeParamToDayjs,
          },
        },
      ],
      defaultToolPanel: "columns",
    };
  }, [refdataAssets]);
  // Methods - END -------------------------------------

  // useEffects - START --------------------------------
  useEffect(() => {
    handleInitialDataLoad();
  }, []);

  useEffect(() => {
    if (rowData.length > 0) {
      setDisplayTable(true);
    } else {
      setDisplayTable(false);
    }
  }, [rowData]);

  useEffect(() => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption(
        "columnDefs",
        millionsFormatting === MILLIONS
          ? initialColDefs
          : currencyFormatterColDefs
      );
    }
  }, [millionsFormatting]);

  useEffect(() => {
    const user_prefs = context_state.user_preferences;

    if (user_prefs) {
      setQueryParams((prev: URLSearchParams) => {
        if (user_prefs.quote_asset) {
          prev.set(QUOTE_ASSET, user_prefs.quote_asset);
        } else {
          prev.delete(QUOTE_ASSET);
        }

        if (user_prefs.interval) {
          prev.set(INTERVAL, user_prefs.interval);
        } else {
          prev.delete(INTERVAL);
        }

        if (user_prefs.start_time) {
          prev.set(START_TIME, user_prefs.start_time);
        } else {
          prev.delete(START_TIME);
        }

        return prev;
      });
    }
  }, [context_state.user_preferences]);
  // useEffects - END ----------------------------------

  return (
    <div className="pulse-sidenav-adjusted-container pulse-bg-color--background-900">
      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        children={<LogoSVG className="pulse-color--background-800" />}
      />

      {alerts.map((alert: IAlertMessage) => {
        return (
          <div className="pulse-mb-1" style={{ width: "100%" }} key={uuidv4()}>
            <AlertMessage
              id={alert.id}
              message={alert.message}
              status={alert.status}
              closeAlertMessage={closeAlert}
            />
          </div>
        );
      })}

      <div className="pulse-row overview-header-row overview-position-relative pulse-mb-3">
        <p className="pulse-typography-headline-6 pulse-color--background-50 pulse-col-md-4 pulse-col-lg-4 pulse-col-xl-4">
          Overview
        </p>
        <div className="overview-switch-container pulse-col-md-8 pulse-col-lg-8 pulse-col-xl-8">
          <p className="pulse-typography-subtitle-1 pulse-color--background-100">
            Dollars
          </p>
          <Switch
            checked={millionsFormatting === MILLIONS}
            onChange={() =>
              setMillionsFormatting(
                millionsFormatting === MILLIONS ? DOLLAR : MILLIONS
              )
            }
          />
          <p className="pulse-typography-subtitle-1 pulse-color--background-100">
            Millions
          </p>
        </div>
      </div>

      {displayTable && (
        <AGTable
          gridRef={gridRef}
          rowData={rowData}
          colDefs={colDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDisplayType="singleColumn"
          sideBar={sideBar}
          animateRows={true}
          grandTotalRows="top"
        />
      )}

      {loading && <Loading />}
    </div>
  );
};

export default Overview;
